import { $ } from "./lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BORDER, CARD, DARK, DIM, MID, RED, SIDEBAR_BG, SIDEBAR_DIM, SIDEBAR_TEXT, css } from "./theme";
import { AIChat } from "./screens/AIChat";
import { CONSENT_VERSION } from "./lib/constants";
import { ClientScorecard } from "./screens/ClientScorecard";
import { ConsentGate } from "./screens/ConsentGate";
import { DEMO_BANNER_SHORT, DEMO_BANNER_TEXT, DEMO_NOT_SAVED_SUFFIX, DEMO_SAVE_NOTE, DEMO_SIGNUP_HREF, DEMO_SIGNUP_LABEL, DEMO_UPLOAD_NOTE, demoMoveItem, isDemoPath } from "./demoMode";
import { DEMO_BUSINESS_NAME, buildDemoData, demoAnswers } from "./demoData";
import { DEMO_DATA_SOURCE, canSave, saveBlockedNote } from "./dataState";
import { Dashboard } from "./screens/Dashboard";
import { DataStateNotice } from "./components/DataStateNotice";
import { JobComparisonModal } from "./components/JobComparisonModal";
import { JobDetail } from "./screens/JobDetail";
import { JobEstimator } from "./screens/JobEstimator";
import { Login } from "./screens/Login";
import { MobileLogoBar } from "./components/MobileLogoBar";
import { OnboardingGate } from "./screens/OnboardingGate";
import { RawData } from "./screens/RawData";
import { Reports } from "./screens/Reports";
import { RevenueGoalModal } from "./components/RevenueGoals";
import { SidebarFooter } from "./components/SidebarFooter";
import { SyncReview } from "./screens/SyncReview";
import { TutorialModal } from "./components/TutorialModal";
import { UploadData } from "./screens/UploadData";
import { VendorSetup } from "./screens/VendorSetup";
import { isIntuitSignInUrl } from "./intuitUrl";
import { settingsTabs, loadHasQuickBooksData, showSettingsEntry } from "./settingsTabs";
import { BusinessTypeContext } from "./useBusinessType";
import { BusinessTypePanel } from "./components/BusinessTypePanel";
import { resolveBusinessType, DEFAULT_BUSINESS_TYPE } from "./businessPreset";
import { saveBusinessType } from "./saveBusinessType";
import { deleteEntry, insertEntry, openWholeJob, periodNoteText, syncSelectedJob, updateEntry } from "./savedEntries";
import { barLabel, moreIsActive, splitNavTabs, usesBottomNav } from "./responsive";
import { DATE_RANGES, filterJobsByDate, filterUntaggedByDate } from "./lib/dates";
import { supabase } from "./lib/supabase";
import { useContractorData } from "./hooks/useContractorData";
import { useDevice } from "./hooks/useDevice";
import { useFirstSyncAfterConnect } from "./hooks/useFirstSyncAfterConnect";
import { fetchProfile } from "./lib/profileColumns";
import { useEffect, useMemo, useRef, useState } from "react";

// Plain-language text for errors we raise ourselves (other codes come from the server's redirect)
const QB_ERROR_TEXT = {
  sign_in_again: "Please sign in again, then try connecting.",
  start_failed:  "We couldn't start the connection. Please try again.",
};

// ─── ROOT APP ─────────────────────────────────────────────────────────────────

export default function App() {
  const device = useDevice();
  const bottomNav = usesBottomNav(device); // phones and tablets use the bottom bar; desktop keeps the sidebar
  const [moreOpen, setMoreOpen] = useState(false);
  const scrollTopKey = useRef("");
  const moreBtnRef = useRef(null);
  const moreSheetRef = useRef(null);
  const wasMoreOpen = useRef(false);
  // When the More sheet closes, focus goes back to the More button.
  useEffect(() => {
    const open = bottomNav && moreOpen;
    if (wasMoreOpen.current && !open && moreBtnRef.current) moreBtnRef.current.focus();
    wasMoreOpen.current = open;
  }, [bottomNav, moreOpen]);
  // The public demo (/demo): no sign-in, no database, no server calls, nothing saved. Decided once, when the page opens.
  const [demo] = useState(() => isDemoPath(window.location.pathname));
  const [demoState, setDemoState] = useState(() => (isDemoPath(window.location.pathname) ? buildDemoData(new Date()) : null));
  // Written answers, worked out from the numbers on screen, so they stay true after a visitor moves an expense.
  const demoAnswerList = useMemo(() => (demoState ? demoAnswers(demoState) : null), [demoState]);
  const [session, setSession]           = useState(null);
  const [profile, setProfile]           = useState(null);
  const [authLoading, setAuthLoading]   = useState(true);
  const [tab, setTab]                   = useState("dashboard");
  const [selectedJob, setSelectedJob]   = useState(null);
  const [periodNote, setPeriodNote]     = useState(null);
  // A new screen (or a different job) starts at the top, not wherever the last page was scrolled to.
  const scrollKey = `${tab}:${selectedJob?.id ?? ""}`;
  useEffect(() => {
    if (scrollTopKey.current === scrollKey) return;
    scrollTopKey.current = scrollKey;
    try { window.scrollTo(0, 0); } catch (e) { /* no scrolling in this environment */ }
  }, [scrollKey]);

  const [tagged, setTagged]             = useState([]);
  const [revenueGoals, setRevenueGoals]       = useState([]);
  const [showGoalModal, setShowGoalModal]     = useState(false);
  const [editingGoal, setEditingGoal]         = useState(null); // null = new goal, else goal object to edit
  const [showComparison, setShowComparison]   = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showTutorial, setShowTutorial]     = useState(false);
  const [dateRange, setDateRange]       = useState("ytd");
  const [customStart, setCustomStart]   = useState("");
  const [customEnd, setCustomEnd]       = useState("");
  const [qbConnected, setQbConnected]       = useState(isDemoPath(window.location.pathname)); // the demo business shows as connected so no "connect" prompts appear
  const [qbError, setQbError]           = useState(null);
  const [qbStarting, setQbStarting]     = useState(false);   // starting the QuickBooks connection
  const [pendingSwitchId, setPendingSwitchId] = useState(null); // held connection to a different QuickBooks company
  const [switchBusy, setSwitchBusy]     = useState(false);
  const [switchError, setSwitchError]   = useState(null);
  const [switchGone, setSwitchGone]     = useState(false); // the held connection no longer exists
  const [switchNames, setSwitchNames]   = useState({ current:null, next:null }); // company names, filled in when they arrive
  const [syncing, setSyncing]           = useState(false);
  const [syncError, setSyncError]       = useState(null);
  const [syncProblems, setSyncProblems] = useState([]);   // steps that did not complete: [{ code, detail }]
  const [showSyncDetails, setShowSyncDetails] = useState(false);
  const [syncNudge, setSyncNudge]       = useState(null);  // { auto, suggested, needs_attention }
  const [showVendorSetup, setShowVendorSetup] = useState(false);
  const [showBusinessType, setShowBusinessType] = useState(false);
  const [vendorSetupIsFirstRun, setVendorSetupIsFirstRun] = useState(false);
  const [qbDataPresent, setQbDataPresent] = useState(undefined); // does this business still have QuickBooks jobs? undefined = checking, null = could not tell
  const [appToast, setAppToast]          = useState(null); // { msg, color }
  const appToastTimer                    = useRef(null);

  // ── Live data hook — loads this account's own data from Supabase. There is no sample data fallback.
  const liveData = useContractorData(demo ? undefined : session?.user?.id); // no account id: it makes no calls
  // First sync right after connecting QuickBooks (reads ?qb_connected before the address is cleaned below)
  useFirstSyncAfterConnect({ demo, userId: session?.user?.id, triggerSync });
  // In the demo the same screens read the demo data, with their own data source so every save is refused.
  const {
    jobSummaries,
    viewState,
    autoMatched,
    suggested,
    untagged: baseUntagged,
    tagged: dbTagged,
    overhead,
    dismissed,
    vendorRules,
    dataSource,
    putSavedEntry,
    dropSavedEntry,
    refresh: refreshData,
  } = demo ? {
    jobSummaries: demoState.jobSummaries, viewState: 'live', autoMatched: demoState.autoMatched, suggested: demoState.suggested,
    untagged: demoState.untagged, tagged: demoState.tagged, overhead: demoState.overhead, dismissed: demoState.dismissed,
    vendorRules: demoState.vendorRules, dataSource: DEMO_DATA_SOURCE, putSavedEntry: () => {}, dropSavedEntry: () => {}, refresh: async () => true,
  } : liveData;

  // Untagged — filter out items tagged during this session (optimistic UI)
  const sessionTaggedIds = new Set(tagged.map(t => t.id));
  const untagged  = baseUntagged.filter(u => !sessionTaggedIds.has(u.id));

  // Combined tagged list: DB-persisted + session-tagged (for display in "All Tagged" tab)
  const allTagged = [...dbTagged, ...tagged.filter(t => !dbTagged.some(d => d.id === t.id))];

  // Keep selectedJob in sync when job summaries update (e.g. after adding labor)
  // Compare costs to avoid infinite loops from object identity changes.
  useEffect(() => {
    if (selectedJob) {
      const { job: fresh, gone } = syncSelectedJob(selectedJob, jobSummaries);
      if (gone && viewState === "live") { setSelectedJob(null); setTab("dashboard"); return; } // its last entry was removed: back to the list
      if (fresh && (fresh.costs !== selectedJob.costs || fresh.revenue !== selectedJob.revenue || fresh.laborCost !== selectedJob.laborCost || fresh.materialCost !== selectedJob.materialCost || fresh.laborEntries !== selectedJob.laborEntries || fresh.manualExpenses !== selectedJob.manualExpenses || fresh.manualRevenue !== selectedJob.manualRevenue || fresh.invoices !== selectedJob.invoices || fresh.purchases !== selectedJob.purchases)) {
        setSelectedJob(fresh);
        setPeriodNote(null); // the dashboard figures may be out of date now
      }
    }
  }, [jobSummaries]); // eslint-disable-line react-hooks/exhaustive-deps

  // The "this month on the dashboard" note describes the period shown when the job was clicked. If the owner changes the
  // period, the note no longer matches the dashboard, so drop it. Clicking a job does not change the period, so this does
  // not clear the note that click just set.
  useEffect(() => { setPeriodNote(null); }, [dateRange, customStart, customEnd]);

  // ── Trigger a QB sync after successful OAuth connect
  async function triggerSync(userId) {
    if (demo) return;
    setSyncing(true);
    setSyncError(null);
    setSyncProblems([]);
    setShowSyncDetails(false);
    try {
      // The server works out who is signed in from the access token, not from the URL.
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setSyncError('Please sign in again to sync.');
        setSyncing(false);
        return;
      }
      const res = await fetch('/api/qb-sync', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.status === 401) {
        setSyncError('Please sign in again to sync.');
      } else if (data.success) {
        console.log('QB sync complete:', data.summary);
        if (Array.isArray(data.problems) && data.problems.length > 0) {
          setSyncProblems(data.problems.map(p => ({ code: String(p.code || ''), detail: String(p.detail || '') })));
        }
        const nowLive = await refreshData();
        setQbDataPresent(undefined); // the sync may have brought QuickBooks data in
        // Show post-sync nudge if matching engine did work
        const m = data.summary?.matching;
        if (m && (m.auto > 0 || m.suggested > 0 || m.needs_attention > 0)) {
          setSyncNudge(m);
        }
        // After sync, if no tracked vendors are set up yet, open vendor setup
        const hasTracked = (vendorRules || []).some(r => r.rule_type === 'tracked');
        if (!hasTracked && nowLive) { // its saves are blocked until the account's own numbers are live
          setVendorSetupIsFirstRun(true);
          setShowVendorSetup(true);
        }
      } else {
        console.error('QB sync failed:', data.error);
        if (data.error === 'QB_DISCONNECTED') {
          setQbConnected(false);
          setQbError('QB_TOKEN_EXPIRED');
        } else {
          setSyncError(data.error || 'Sync failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('QB sync error:', err);
      setSyncError('Sync timed out or lost connection. Please try again.');
    }
    setSyncing(false);
  }

  // ── Start the QuickBooks connection. The server needs to know who is signed in, so we
  // ask it for the Intuit address with the access token, then send the browser there.
  async function connectQuickBooks() {
    if (demo || qbStarting) return;
    setQbStarting(true);
    setQbError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setQbError('sign_in_again');
        setQbStarting(false);
        return;
      }
      const res = await fetch('/api/qb-connect', { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        setQbError('sign_in_again');
        setQbStarting(false);
        return;
      }
      const data = await res.json();
      if (!res.ok || !data.url) {
        setQbError('start_failed');
        setQbStarting(false);
        return;
      }
      // Only ever leave the app for Intuit's own sign-in page, over https.
      if (!isIntuitSignInUrl(data.url)) {
        setQbError('start_failed');
        setQbStarting(false);
        return;
      }
      window.location.assign(new URL(data.url).href); // leaves the app; the button stays disabled
    } catch (err) {
      console.error('QB connect error:', err.message);
      setQbError('start_failed');
      setQbStarting(false);
    }
  }

  // ── Answer the "different QuickBooks company" question. For now the only choice is 'keep'
  // (one QuickBooks company per business; adding more businesses is a later task).
  async function answerSwitch(choice) {
    if (demo || switchBusy || !pendingSwitchId) return;
    if (switchGone) { setPendingSwitchId(null); return; }
    setSwitchBusy(true);
    setSwitchError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setSwitchError('Please sign in again, then connect again.');
        setSwitchBusy(false);
        return;
      }
      const res = await fetch('/api/qb-switch', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingId: pendingSwitchId, choice }),
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (res.status === 410 || res.status === 404) {
        setSwitchGone(true);
        setSwitchError('That took too long. Please connect again.');
      } else if (res.status === 401) {
        setSwitchError('Please sign in again, then connect again.');
      } else if (res.ok && data?.success) {
        setPendingSwitchId(null);
        showAppToast(switchNames.current ? `Kept ${switchNames.current}` : 'Kept your current QuickBooks company', ACCENT2);
      } else {
        setSwitchError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('QB switch error:', err.message);
      setSwitchError('Something went wrong. Please try again.');
    }
    setSwitchBusy(false);
  }

  // Look up the company names when the question opens.
  // The modal shows right away with generic wording; names fill in if they arrive.
  useEffect(() => {
    if (demo || !pendingSwitchId) return;
    setSwitchNames({ current:null, next:null });
    setSwitchGone(false);
    setSwitchError(null);
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) return;
        const res = await fetch(`/api/qb-switch?pendingId=${encodeURIComponent(pendingSwitchId)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (res.status === 410 || res.status === 404) {
          setSwitchGone(true);
          setSwitchError('That took too long. Please connect again.');
          return;
        }
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setSwitchNames({ current: d.currentCompanyName || null, next: d.newCompanyName || null });
      } catch { /* names are optional */ }
    })();
    return () => { cancelled = true; };
  }, [pendingSwitchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Revenue goal CRUD
  async function handleSaveGoal(goalData) {
    if (demo) { showAppToast(DEMO_SAVE_NOTE, AMBER); return; }
    if (!session?.user?.id) return;
    if (goalData?.id) {
      const { data } = await supabase.from("revenue_goals")
        .update({ label:goalData.label, period_type:goalData.period_type, target_amount:goalData.target_amount, start_date:goalData.start_date, end_date:goalData.end_date })
        .eq("id", goalData.id).select().single();
      if (data) setRevenueGoals(prev => prev.map(g => g.id === data.id ? data : g));
    } else {
      const { data } = await supabase.from("revenue_goals")
        .insert({ label:goalData.label, period_type:goalData.period_type, target_amount:goalData.target_amount, start_date:goalData.start_date, end_date:goalData.end_date, contractor_id:session.user.id, status:"active" })
        .select().single();
      if (data) setRevenueGoals(prev => [data, ...prev]);
    }
  }
  async function handleDeleteGoal(goalId) {
    if (demo) { showAppToast(DEMO_SAVE_NOTE, AMBER); return; }
    await supabase.from("revenue_goals").delete().eq("id", goalId);
    setRevenueGoals(prev => prev.filter(g => g.id !== goalId));
  }
  async function handleMarkCompleteGoal(goalId) {
    if (demo) { showAppToast(DEMO_SAVE_NOTE, AMBER); return; }
    const { data } = await supabase.from("revenue_goals").update({ status:"complete" }).eq("id", goalId).select().single();
    if (data) setRevenueGoals(prev => prev.map(g => g.id === goalId ? data : g));
  }
  async function handleArchiveGoal(goalId) {
    if (demo) { showAppToast(DEMO_SAVE_NOTE, AMBER); return; }
    const { data } = await supabase.from("revenue_goals").update({ status:"archived" }).eq("id", goalId).select().single();
    if (data) setRevenueGoals(prev => prev.map(g => g.id === goalId ? data : g));
  }

  // ── On mount: check session + handle QB OAuth redirect params
  useEffect(() => {
    if (demo) { setAuthLoading(false); return undefined; } // the demo never checks for a sign-in
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') {
      setQbConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('qb_disconnected') === 'true') {
      setQbConnected(false);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('qb_error')) {
      setQbError(params.get('qb_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('qb_confirm_switch')) {
      setPendingSwitchId(params.get('qb_confirm_switch'));
      window.history.replaceState({}, '', window.location.pathname);
    }

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s) {
        // Only the columns the app uses (not the quote logo); see src/lib/profileColumns.js.
        const p = await fetchProfile(supabase, s.user.id);
        setProfile(p);
        if (p?.qb_realm_id) setQbConnected(true);
        const { data: goals } = await supabase.from("revenue_goals").select("*").eq("contractor_id", s.user.id).order("created_at", { ascending: false });
        if (goals) setRevenueGoals(goals);
        // (The first sync after connecting QuickBooks is started by useFirstSyncAfterConnect.)
        const dismissed = localStorage.getItem(`canopy_disclaimer_${s.user.id}`);
        if (!dismissed) setShowDisclaimer(true);
        // Show tutorial on first login
        const tutorialSeen = localStorage.getItem(`canopy_tutorial_${s.user.id}`);
        if (!tutorialSeen) setShowTutorial(true);
      }
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) { setProfile(null); setTab("dashboard"); setShowDisclaimer(false); setQbConnected(false); setQbDataPresent(undefined); }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleLogin(user, contractorProfile) {
    setSession({ user });
    setProfile(contractorProfile);
    if (contractorProfile?.qb_realm_id) setQbConnected(true);
    const dismissed = localStorage.getItem(`canopy_disclaimer_${user.id}`);
    if (!dismissed) setShowDisclaimer(true);
  }

  function dismissDisclaimer() {
    if (session?.user?.id) localStorage.setItem(`canopy_disclaimer_${session.user.id}`, 'true');
    setShowDisclaimer(false);
  }

  function dismissTutorial() {
    if (session?.user?.id) localStorage.setItem(`canopy_tutorial_${session.user.id}`, 'true');
    setShowTutorial(false);
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setSession(null); setProfile(null); setTab("dashboard");
    setShowDisclaimer(false); setQbConnected(false); setQbError(null); setQbDataPresent(undefined);
  }

  // ── Show nothing while checking session on first load
  if (authLoading) {
    return (
      <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <style>{css}</style>
        <div style={{ fontFamily:"'Lora',serif", fontSize:16, color:DIM, fontStyle:"italic" }}>Loading...</div>
      </div>
    );
  }

  if (!session && !demo) return <Login onLogin={handleLogin} />;

  // Consent gate — shown if user has never consented, or consented to an older policy version
  const needsConsent = !demo && (!profile?.consent_accepted_at || profile?.consent_version !== CONSENT_VERSION);
  if (needsConsent) {
    return (
      <ConsentGate
        userId={session.user.id}
        onConsent={(fields) => setProfile(p => ({ ...p, ...fields }))}
      />
    );
  }

  // Onboarding gate — shown once when industry is null (captures industry + revenue_range)
  const needsOnboarding = !demo && !profile?.industry;
  if (needsOnboarding) {
    return (
      <OnboardingGate
        userId={session.user.id}
        onComplete={(fields) => setProfile(p => ({ ...p, ...fields }))}
      />
    );
  }

  const clientType     = demo ? "quickbooks" : (profile?.client_type || "quickbooks");
  // How costs are labelled and whether missing labor counts. The demo is always Trades. Before the database
  // migration is applied the profile has no saved choice, so the industry picked at sign-up decides.
  const businessType   = demo ? DEFAULT_BUSINESS_TYPE : resolveBusinessType(profile);
  const contractorName = demo ? DEMO_BUSINESS_NAME : (profile?.name || session?.user?.email || "Your Account");

  async function handleSaveBusinessType(key, paysPeople) {
    const result = await saveBusinessType({ client: supabase, userId: session?.user?.id, demo, key, paysPeople });
    if (result.ok) {
      setProfile(p => ({ ...p, ...result.row }));
      showAppToast("Business type saved");
    }
    return result;
  }

  function showAppToast(msg, color = ACCENT2) {
    clearTimeout(appToastTimer.current);
    setAppToast({ msg, color });
    appToastTimer.current = setTimeout(() => setAppToast(null), 3000);
  }

  // Every database write goes through this. Returns true (and tells the owner) when the write must not happen
  // because the numbers on screen are not the account's own live data, or the ids are old sample ids.
  function blockUnsavable(...ids) {
    if (canSave(dataSource, ...ids)) return false;
    showAppToast(saveBlockedNote(dataSource, DEMO_SAVE_NOTE), AMBER);
    return true;
  }

  // Demo only: move a review item on screen. Job costs follow. Nothing is saved, and a reload starts over.
  function demoMove(message, itemId, target, jobId) {
    setDemoState(prev => demoMoveItem(prev, itemId, target, jobId));
    showAppToast(`${message}. ${DEMO_NOT_SAVED_SUFFIX}`, ACCENT2);
  }

  function handleJobClick(job) {
    // Always open the whole job, not the slice of it the dashboard period showed.
    const { job: whole, period } = openWholeJob(job, jobSummaries);
    const label = dateRange === "custom" ? "The chosen dates" : (DATE_RANGES.find(r => r.key === dateRange)?.label || null);
    setPeriodNote(periodNoteText(period, label, $));
    setSelectedJob(whole);
    setTab("detail");
  }

  // Entries made in Canopy (labor, cash costs, added revenue). Saved to the database first; an entry is shown (and
  // counted) only after the save worked. Each returns true when it worked, so the form knows whether to close.
  // In the demo nothing is saved (blockUnsavable says so).
  async function saveEntryFor(kind, entry, label) {
    if (blockUnsavable(entry.jobId || undefined)) return false;
    const result = await insertEntry({ client: supabase, userId: session?.user?.id, kind, entry });
    if (!result.ok) { showAppToast(result.message, RED); return false; }
    putSavedEntry(kind, result.entry);
    showAppToast(`${label} added: ${$(result.entry.amount)}`, ACCENT2);
    return true;
  }
  async function updateEntryFor(kind, id, entry, label) {
    if (blockUnsavable(entry.jobId || undefined)) return false;
    const result = await updateEntry({ client: supabase, userId: session?.user?.id, kind, id, entry });
    if (!result.ok) { showAppToast(result.message, RED); return false; }
    putSavedEntry(kind, result.entry);
    showAppToast(`${label} updated: ${$(result.entry.amount)}`, ACCENT2);
    return true;
  }
  async function deleteEntryFor(kind, id, message) {
    if (blockUnsavable()) return false;
    const result = await deleteEntry({ client: supabase, userId: session?.user?.id, kind, id });
    if (!result.ok) { showAppToast(result.message, RED); return false; }
    dropSavedEntry(kind, id);
    showAppToast(message, DIM);
    return true;
  }

  const handleAddLabor      = (entry) => saveEntryFor("labor", entry, "Labor entry");
  const handleUpdateLabor   = (id, entry) => updateEntryFor("labor", id, entry, "Labor entry");
  const handleDeleteLabor   = (id) => deleteEntryFor("labor", id, "Labor entry removed");
  const handleAddExpense    = (entry) => saveEntryFor("expense", entry, "Expense");
  const handleUpdateExpense = (id, entry) => updateEntryFor("expense", id, entry, "Expense");
  const handleDeleteExpense = (id) => deleteEntryFor("expense", id, "Expense removed");
  const handleAddRevenue    = (entry) => saveEntryFor("revenue", entry, "Revenue");
  const handleUpdateRevenue = (id, entry) => updateEntryFor("revenue", id, entry, "Revenue");
  const handleDeleteRevenue = (id) => deleteEntryFor("revenue", id, "Revenue entry removed");

  async function handleUpdateJobType(jobId, newType) {
    if (blockUnsavable(jobId)) return;
    // Update selectedJob immediately for responsive UI
    if (selectedJob && selectedJob.id === jobId) {
      setSelectedJob(prev => ({ ...prev, type: newType }));
    }
    // Persist to Supabase jobs table
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ job_type: newType })
        .eq('id', jobId);
      if (error) {
        console.error('Job type update error:', error.message);
        showAppToast("Failed to save job type", RED);
      } else {
        showAppToast(`Job type → ${newType}`, ACCENT2);
      }
    } catch (e) {
      console.error('Job type update error:', e.message);
    }
  }

  async function handleTag(item, jobId, jobName) {
    if (demo) { demoMove(`Assigned to ${jobName}`, item.id, "tagged", jobId); return; }
    if (blockUnsavable(item.id, jobId)) return;
    // Optimistically update local state immediately so UI feels instant
    setTagged(prev => [...prev, { ...item, taggedJobId: jobId, taggedJobName: jobName }]);
    if (selectedJob && selectedJob.id === jobId) {
      const updated = jobSummaries.find(j => j.id === jobId);
      if (updated) setSelectedJob(updated);
    }

    // Write tag to Supabase — update inbox_tags row status and linked job.
    // supabase-js returns { error } instead of throwing, so check each step.
    try {
      // Cost row first, so a failure can never leave an item tagged with no cost
      const { error: costError } = await supabase
        .from('transactions')
        .upsert({
          id:            `${session.user.id}_inbox_${item.id}`,
          contractor_id: session.user.id,
          job_id:        jobId,
          type:          'expense',
          doc_number:    item.docNumber,
          txn_date:      item.date,
          amount:        item.amount,
          description:   item.description,
          vendor:        item.vendor,
        }, { onConflict: 'id' });
      if (costError) throw costError;

      // Then mark the review row tagged
      const { error: tagError } = await supabase
        .from('inbox_tags')
        .update({ status: 'tagged', tagged_job_id: jobId })
        .eq('id', item.id);
      if (tagError) throw tagError;

      // Only now remove any auto-match cost row for the same item, so it is not counted twice
      const { error: autoError } = await supabase
        .from('transactions')
        .delete()
        .eq('contractor_id', session.user.id)
        .eq('id', `${session.user.id}_automatch_${item.id}`);
      if (autoError) throw autoError;

      // Refresh live data so Data Quality Score and job costs update
      await refreshData();
      showAppToast(`Tagged to ${jobName}`);
    } catch (err) {
      console.error('Error saving tag to Supabase:', err);
      // Roll back optimistic update so item reappears in inbox
      setTagged(prev => prev.filter(t => t.id !== item.id));
      showAppToast('Failed to save tag — please try again', RED);
    }
  }

  async function handleMarkOverhead(item) {
    if (demo) { demoMove("Marked as overhead", item.id, "overhead"); return; }
    if (blockUnsavable(item.id)) return;
    // Optimistically remove from untagged
    setTagged(prev => prev.filter(u => u.id !== item.id));

    try {
      const { error: statusError } = await supabase
        .from('inbox_tags')
        .update({ status: 'overhead' })
        .eq('id', item.id);
      if (statusError) throw statusError;

      // A fixed cost is not job cost: remove any auto-match cost row already on a job
      const { error: autoError } = await supabase
        .from('transactions')
        .delete()
        .eq('contractor_id', session.user.id)
        .eq('id', `${session.user.id}_automatch_${item.id}`);
      if (autoError) throw autoError;

      await refreshData();
      showAppToast('Marked as fixed cost overhead');
    } catch (err) {
      console.error('Error marking as overhead:', err);
      showAppToast('Failed to update — please try again', RED);
    }
  }

  async function handleDismiss(id) {
    if (demo) { demoMove("Expense dismissed", id, "dismissed"); return; }
    if (blockUnsavable(id)) return;
    setTagged(prev => prev.filter(u => u.id !== id));
    try {
      const { error: statusError } = await supabase
        .from('inbox_tags')
        .update({ status: 'dismissed' })
        .eq('id', id);
      if (statusError) throw statusError;
      // Remove any auto-match cost row already on a job
      const { error: autoError } = await supabase
        .from('transactions')
        .delete()
        .eq('contractor_id', session.user.id)
        .eq('id', `${session.user.id}_automatch_${id}`);
      if (autoError) throw autoError;
      await refreshData();
      showAppToast('Expense dismissed', AMBER);
    } catch (err) {
      console.error('Error saving dismissal to Supabase:', err);
      showAppToast('Failed to dismiss — please try again', RED);
    }
  }

  async function handleRestore(id) {
    if (demo) { demoMove("Expense restored to the list", id, "untagged"); return; }
    if (blockUnsavable(id)) return;
    try {
      await supabase
        .from('inbox_tags')
        .update({ status: 'pending' })
        .eq('id', id);
      await refreshData();
      showAppToast('Expense restored to inbox');
    } catch (err) {
      console.error('Error restoring expense:', err);
      showAppToast('Failed to restore — please try again', RED);
    }
  }

  // ── Sync Review handlers ──────────────────────────────────────────────────────

  async function handleConfirmSuggestion(item, jobId, jobName) {
    if (demo) { demoMove(`Confirmed and assigned to ${jobName}`, item.id, "tagged", jobId); return; }
    if (blockUnsavable(item.id, jobId)) return;
    // Accept a suggested or auto-assigned match — works for both confirm and change-job
    setTagged(prev => [...prev, { ...item, taggedJobId: jobId, taggedJobName: jobName }]);
    try {
      // Cost row first, so a failure can never leave an item tagged with no cost
      const { error: costError } = await supabase
        .from('transactions')
        .upsert({
          id:            `${session.user.id}_inbox_${item.id}`,
          contractor_id: session.user.id,
          job_id:        jobId,
          type:          'expense',
          doc_number:    item.docNumber,
          txn_date:      item.date,
          amount:        item.amount,
          description:   item.description,
          vendor:        item.vendor,
        }, { onConflict: 'id' });
      if (costError) throw costError;
      // Then mark the review row tagged
      const { error: tagError } = await supabase
        .from('inbox_tags')
        .update({ status: 'tagged', tagged_job_id: jobId, matched_by: 'manual' })
        .eq('id', item.id);
      if (tagError) throw tagError;
      // Only now remove any auto-match cost row for the same item, so it is not counted twice
      const { error: autoError } = await supabase
        .from('transactions')
        .delete()
        .eq('contractor_id', session.user.id)
        .eq('id', `${session.user.id}_automatch_${item.id}`);
      if (autoError) throw autoError;
      await refreshData();
      showAppToast(`Confirmed — tagged to ${jobName}`);
    } catch (err) {
      console.error('Error confirming suggestion:', err);
      // Roll back optimistic update so item reappears
      setTagged(prev => prev.filter(t => t.id !== item.id));
      showAppToast('Failed to confirm — please try again', RED);
    }
  }

  async function handleRetag(item, newJobId, newJobName) {
    if (demo) { demoMove(`Moved to ${newJobName}`, item.id, "tagged", newJobId); return; }
    if (blockUnsavable(item.id, newJobId)) return;
    // Change a tagged/auto-matched expense to a different job.
    // Insert new transaction FIRST, then clean up old rows — so if insert fails,
    // the old transaction stays intact and job costs are never orphaned.
    try {
      // 1. Insert new transaction for the correct job (upsert overwrites _inbox_ row if it exists)
      await supabase
        .from('transactions')
        .upsert({
          id:            `${session.user.id}_inbox_${item.id}`,
          contractor_id: session.user.id,
          job_id:        newJobId,
          type:          'expense',
          doc_number:    item.docNumber,
          txn_date:      item.date,
          amount:        item.amount,
          description:   item.description,
          vendor:        item.vendor,
        }, { onConflict: 'id' });

      // 2. Clean up any leftover auto-match transaction (different ID pattern)
      await supabase
        .from('transactions')
        .delete()
        .eq('id', `${session.user.id}_automatch_${item.id}`);

      // 3. Update inbox_tags
      await supabase
        .from('inbox_tags')
        .update({ status: 'tagged', tagged_job_id: newJobId, matched_by: 'manual' })
        .eq('id', item.id);

      await refreshData();
      showAppToast(`Retagged to ${newJobName}`);
    } catch (err) {
      console.error('Error re-tagging expense:', err);
      showAppToast('Failed to retag — please try again', RED);
    }
  }

  async function handleUndoAutoMatch(item) {
    if (demo) { demoMove("Match undone, moved back to needs attention", item.id, "untagged"); return; }
    if (blockUnsavable(item.id)) return;
    // Move an auto-matched item back to pending (needs attention)
    try {
      await supabase
        .from('inbox_tags')
        .update({ status: 'pending', tagged_job_id: null, match_tier: 'needs_attention', matched_by: null })
        .eq('id', item.id);

      // Remove the auto-matched transaction row
      const autoTxnId = `${session.user.id}_automatch_${item.id}`;
      await supabase.from('transactions').delete().eq('id', autoTxnId);

      await refreshData();
      showAppToast('Auto-match undone — moved back to needs attention', AMBER);
    } catch (err) {
      console.error('Error undoing auto-match:', err);
      showAppToast('Failed to undo — please try again', RED);
    }
  }

  // ── Vendor rule management ────────────────────────────────────────────────────

  // Settings opens for every signed-in owner. Which tabs show depends on whether QuickBooks data is here.
  function openSettings() {
    setVendorSetupIsFirstRun(false);
    setQbDataPresent(undefined);
    setShowVendorSetup(true);
    if (session?.user?.id) loadHasQuickBooksData(supabase, session.user.id).then(setQbDataPresent);
  }

  async function handleVendorSetupSave() {
    if (demo) { setShowVendorSetup(false); return; }
    setShowVendorSetup(false);
    await refreshData();
    // On first run, trigger a clean resync so the vendor filter takes effect immediately
    if (vendorSetupIsFirstRun && session?.user?.id) {
      triggerSync(session.user.id);
    }
    setVendorSetupIsFirstRun(false);
  }

  async function handleSaveVendorRule(vendorName, ruleType) {
    if (demo) { showAppToast(DEMO_SAVE_NOTE, AMBER); return; }
    if (!session?.user?.id || !vendorName || !ruleType) return;
    if (blockUnsavable()) return;
    try {
      await supabase
        .from('vendor_rules')
        .upsert({
          contractor_id: session.user.id,
          vendor_name:   vendorName,
          rule_type:     ruleType,
        }, { onConflict: 'contractor_id,vendor_name' });
      await refreshData();
    } catch (err) {
      console.error('Error saving vendor rule:', err);
    }
  }

  const reviewItems = [
    ...filterUntaggedByDate(untagged,        dateRange, customStart, customEnd),
    ...filterUntaggedByDate(suggested || [], dateRange, customStart, customEnd),
  ];
  const reviewCount = reviewItems.length;
  const qualityJobs = dateRange === "all" ? jobSummaries : filterJobsByDate(jobSummaries, dateRange, customStart, customEnd);

  // SVG icon paths — lightweight inline icons (Lucide-inspired, 18×18 viewBox)
  const IC = (d, sz=18) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, opacity:0.7 }}>{d}</svg>;
  const ICONS = {
    dashboard: IC(<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>),
    clients:   IC(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>),
    inbox:     IC(<><path d="M22 12h-6l-2 3H10l-2-3H2"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>),
    detail:    IC(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>),
    estimator: IC(<><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>),
    reports:   IC(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>),
    chat:      IC(<><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></>),
    raw:       IC(<><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></>),
    gear:      IC(<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></>),
    upload:    IC(<><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><polyline points="7 9 12 4 17 9"/><line x1="12" y1="4" x2="12" y2="16"/></>),
    more:      IC(<><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></>, 22),
  };

  const TABS = [
    { key:"dashboard", label:"Dashboard",         icon:ICONS.dashboard },
    { key:"clients",   label:"Clients",           icon:ICONS.clients },
    ...(clientType === "quickbooks" ? [{ key:"inbox", label:"Expense Management", icon:ICONS.inbox }] : []),
    { key:"detail",    label:"Job Detail",        icon:ICONS.detail },
    { key:"estimator", label:"Quote Generator",   icon:ICONS.estimator },
    { key:"reports",   label:"Reports",           icon:ICONS.reports },
    { key:"chat",      label:"AI Analyst",        icon:ICONS.chat },
    ...(clientType === "quickbooks" ? [{ key:"raw", label:"Raw Data",             icon:ICONS.raw }] : []),
    { key:"upload",   label:"Upload Data",        icon:ICONS.upload },
  ];

  // Bottom bar: the four most used tabs, then More for the rest (see src/responsive.js).
  const navSplit = splitNavTabs(TABS);
  const closeMore = () => setMoreOpen(false);
  // Escape closes the sheet; Tab and Shift+Tab stay inside it while it is open.
  const moreSheetKeys = (e) => {
    if (e.key === "Escape") { closeMore(); return; }
    if (e.key !== "Tab" || !moreSheetRef.current) return;
    const items = Array.from(moreSheetRef.current.querySelectorAll("button, a[href]"));
    if (items.length === 0) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  const goTab = (key) => { setTab(key); setMoreOpen(false); };
  // The More sheet only exists on phones and tablets. If the window grows to desktop size it closes.
  const moreSheetOpen = bottomNav && moreOpen;

  return (
    <BusinessTypeContext.Provider value={businessType}>
    <div className="app-root" style={{ fontFamily:"'DM Sans',sans-serif", background:BG, minHeight:"100vh", color:DARK, display:"flex", flexDirection:"row" }}>
      <style>{css}</style>

      {/* ── Tutorial modal ── */}
      {showTutorial && <TutorialModal onClose={dismissTutorial} qbConnected={qbConnected} clientType={clientType}/>}

      {/* ── Business type panel ── */}
      {showBusinessType && !demo && (
        <BusinessTypePanel businessType={businessType} onSave={handleSaveBusinessType} onClose={() => setShowBusinessType(false)} />
      )}

      {/* ── Vendor setup modal ── */}
      {showVendorSetup && !demo && (
        <VendorSetup
          userId={session.user.id}
          vendorRules={vendorRules}
          onSave={handleVendorSetupSave}
          onClose={() => setShowVendorSetup(false)}
          isFirstRun={vendorSetupIsFirstRun}
          syncing={syncing}
          qbConnected={qbConnected}
          checkFailed={qbDataPresent === null && !qbConnected}
          tabs={qbDataPresent === undefined ? null : settingsTabs({ qbConnected, hasQbData: qbDataPresent })}
        />
      )}

      {/* ── Different QuickBooks company: tell the owner it was not connected ── */}
      {pendingSwitchId && (
        <div role="dialog" aria-modal="true" aria-labelledby="qb-switch-title" aria-describedby="qb-switch-body" onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); answerSwitch('keep'); } }} style={{ position:"fixed", inset:0, background:"rgba(44,36,22,0.5)", zIndex:510, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div className="r-gate" style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:8, width:"100%", maxWidth:520, boxShadow:"0 20px 60px rgba(44,36,22,0.2)", padding:"32px 36px" }}>
            <h2 id="qb-switch-title" style={{ fontFamily:"'Lora',serif", fontSize:18, fontWeight:500, color:DARK, marginBottom:14 }}>This is a different QuickBooks company</h2>
            <p id="qb-switch-body" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:MID, lineHeight:1.75, marginBottom:24 }}>{switchNames.current || 'Your current QuickBooks company'} is already connected here. You picked {switchNames.next || 'the company you picked'}. For now, each business in Canopy connects to one QuickBooks company, so {switchNames.next || 'the company you picked'} wasn't connected. Adding more businesses is coming soon.</p>
            {switchError && <p role="alert" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:RED, marginBottom:16 }}>{switchError}</p>}
            <div className="r-btnrow" style={{ borderTop:`1px solid ${BORDER}`, paddingTop:20, display:"flex", justifyContent:"flex-end", gap:10 }}>
              <button autoFocus className="btn act" disabled={switchBusy} onClick={() => answerSwitch('keep')} style={{ padding:"9px 24px", fontSize:12 }}>{switchGone ? 'Close' : (switchNames.current ? `Keep ${switchNames.current}` : 'Keep current company')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── First-login disclaimer modal ── */}
      {showDisclaimer && (
        <div role="dialog" aria-modal="true" aria-label="Data disclaimer" className="r-overlay" onKeyDown={e => e.key === 'Escape' && setShowDisclaimer(false)} style={{ position:"fixed", inset:0, background:"rgba(44,36,22,0.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div className="r-sheet r-sheet-pad" style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:8, width:"100%", maxWidth:520, boxShadow:"0 20px 60px rgba(44,36,22,0.2)", padding:"32px 36px" }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14, marginBottom:20 }}>
              <div style={{ width:36, height:36, borderRadius:6, background:"rgba(140,107,48,0.1)", border:`1px solid rgba(140,107,48,0.25)`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:16 }}>ℹ</div>
              <div>
                <h2 style={{ fontFamily:"'Lora',serif", fontSize:18, fontWeight:500, color:DARK, marginBottom:4 }}>A note about your data</h2>
                <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM }}>Please read before using your dashboard</p>
              </div>
            </div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:MID, lineHeight:1.75, marginBottom:24 }}>
              {clientType === "quickbooks" ? (<>
                <p style={{ marginBottom:12 }}>The figures shown in Canopy are derived directly from your <strong style={{ color:DARK }}>QuickBooks Online account</strong>. Canopy does not modify, verify, or audit your QuickBooks data — any inaccuracies or incomplete records in QuickBooks will be reflected here.</p>
                <p style={{ marginBottom:12 }}>Common sources of inaccurate data include <strong style={{ color:DARK }}>untagged expenses</strong> (visible in Expense Management), missing invoices, or duplicate entries. Your Data Quality Score on the dashboard indicates how complete your records are.</p>
              </>) : (<>
                <p style={{ marginBottom:12 }}>The figures shown in Canopy come from the <strong style={{ color:DARK }}>data you uploaded or entered</strong>. Canopy does not verify or audit that data — any mistakes or missing records in it will be reflected here.</p>
                <p style={{ marginBottom:12 }}>Common sources of inaccurate data include costs left off a job, missing invoices, or duplicate entries.</p>
              </>)}
              <p>Canopy is provided for <strong style={{ color:DARK }}>informational purposes only</strong> and does not constitute financial, tax, or accounting advice. Always consult your accountant or bookkeeper before making significant business decisions.</p>
            </div>
            <div className="r-stack" style={{ borderTop:`1px solid ${BORDER}`, paddingTop:20, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div style={{ fontSize:11, color:DIM, fontFamily:"'DM Sans',sans-serif" }}>This notice will not appear again after dismissal.</div>
              <button className="btn act" onClick={dismissDisclaimer} style={{ padding:"9px 24px", fontSize:12 }}>I understand — continue</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar — hidden on mobile, replaced by bottom nav ── */}
      <div style={{ width:220, flexShrink:0, background:SIDEBAR_BG, height:"100vh", position:"sticky", top:0, display: bottomNav ? "none" : "flex", flexDirection:"column", overflowY:"auto", zIndex:100 }}>
        {/* Logo */}
        <div style={{ padding:"24px 20px 20px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ fontFamily:"'Lora',serif", fontSize:18, fontWeight:500, color:"#F5EFE3", letterSpacing:"-0.01em" }}>Canopy</div>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, letterSpacing:"0.03em", color:SIDEBAR_DIM, marginTop:2 }}>Business Intelligence</div>
        </div>
        {/* Nav items */}
        <nav style={{ flex:1, padding:"12px 0" }}>
          {TABS.map(t => (
            <div key={t.key} className={`si${tab===t.key?" active":""}`} onClick={()=>setTab(t.key)}
              style={{ color: tab===t.key ? "#F5EFE3" : SIDEBAR_TEXT }}>
              {t.icon}
              <span style={{ flex:1 }}>{t.label}</span>
              {t.key==="inbox" && reviewCount > 0 && <span className="badge">{reviewCount}</span>}
              {t.key==="inbox" && reviewCount === 0 && allTagged.length > 0 && <span className="badge done">✓</span>}
              {t.key==="detail" && selectedJob && <span style={{ fontSize:10, color:SIDEBAR_DIM }}>· {selectedJob.name.split(" ")[0]}</span>}
              {t.key==="chat" && <span style={{ fontSize:9, padding:"2px 6px", borderRadius:3, background:"rgba(92,122,90,0.25)", color:ACCENT2, fontWeight:500 }}>AI</span>}
            </div>
          ))}
        </nav>
        {/* Getting Started checklist — shown until all 3 steps complete */}
        {clientType === "quickbooks" && !demo && (() => {
          const step1 = qbConnected;
          const step2 = allTagged.length > 0 || (autoMatched || []).length > 0;
          const step3 = jobSummaries.length > 0 && dataSource === 'live';
          const allDone = step1 && step2 && step3;
          if (allDone) return null;
          const steps = [
            { label: "Connect QuickBooks", done: step1 },
            { label: "Review your sync",   done: step2 },
            { label: "View profitability", done: step3 },
          ];
          return (
            <div style={{ margin:"0 12px 10px", borderRadius:6, border:"1px solid rgba(255,255,255,0.09)", background:"rgba(255,255,255,0.04)", padding:"12px 14px" }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(245,239,227,0.4)", marginBottom:10 }}>Getting Started</div>
              {steps.map((s, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:9, marginBottom:7 }}>
                  <div style={{ width:16, height:16, borderRadius:"50%", border:`1.5px solid ${s.done ? ACCENT2 : "rgba(255,255,255,0.2)"}`, background: s.done ? `${ACCENT2}22` : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:9, color:ACCENT2 }}>
                    {s.done ? "✓" : ""}
                  </div>
                  <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color: s.done ? "rgba(245,239,227,0.45)" : "rgba(245,239,227,0.75)", textDecoration: s.done ? "line-through" : "none" }}>{s.label}</span>
                </div>
              ))}
            </div>
          );
        })()}

        <SidebarFooter
          name={contractorName} isQuickBooks={clientType === "quickbooks"} qbConnected={qbConnected} syncing={syncing} demo={demo}
          hasTrackedRules={(vendorRules||[]).some(r=>r.rule_type==='tracked') || !(qbDataPresent === true || (qbDataPresent == null && qbConnected))} gearIcon={ICONS.gear}
          onSync={() => triggerSync(session?.user?.id)}
          onOpenSettings={showSettingsEntry({ demo, signedIn: !!session }) ? openSettings : undefined}
          onOpenBusinessType={() => setShowBusinessType(true)}
          onOpenHelp={() => setShowTutorial(true)} onSignOut={handleSignOut} />
      </div>

      {/* Right content wrapper */}
      <div className="app-main" style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, position:"relative" }}>

      {bottomNav && <MobileLogoBar name={contractorName} onOpenMore={() => setMoreOpen(true)}/>}

      {demo && (
        <div role="status" className="demo-banner app-banner" style={{ position:"sticky", top:0, zIndex:120, background:"#F3ECDD", borderBottom:`1px solid rgba(140,107,48,0.35)`, padding:"10px 36px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:DARK, lineHeight:1.5 }}><span className="demo-full">{DEMO_BANNER_TEXT}</span><span className="demo-short">{DEMO_BANNER_SHORT}</span></div>
          <a href={DEMO_SIGNUP_HREF} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:500, color:CARD, background:ACCENT, padding:"7px 16px", borderRadius:4, textDecoration:"none", whiteSpace:"nowrap" }}>{DEMO_SIGNUP_LABEL}</a>
        </div>
      )}

      {appToast && (
        <div className="app-toast" style={{ position:"fixed", bottom:32, left:"50%", transform:"translateX(-50%)", zIndex:900, background:DARK, color:"#F5EFE3", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:500, padding:"11px 22px", borderRadius:7, boxShadow:"0 6px 24px rgba(44,36,22,0.22)", border:`1.5px solid ${appToast.color}`, display:"flex", alignItems:"center", gap:10, pointerEvents:"none" }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:appToast.color, flexShrink:0 }}/>
          {appToast.msg}
        </div>
      )}

      {qbError && (
        <div className="app-banner" style={{ background:"rgba(140,64,64,0.06)", borderBottom:`1px solid rgba(140,64,64,0.2)`, padding:"11px 36px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div style={{ fontSize:13, color:RED, fontFamily:"'DM Sans',sans-serif" }}>
            <span style={{ fontWeight:500 }}>QuickBooks connection failed</span>
            <span style={{ color:MID, marginLeft:8 }}>— {QB_ERROR_TEXT[qbError] || `${qbError.replace(/_/g,' ')}. Please try connecting again.`}</span>
          </div>
          <button onClick={()=>setQbError(null)} style={{ background:"none", border:"none", cursor:"pointer", color:DIM, fontSize:16, padding:"0 4px" }}>x</button>
        </div>
      )}

      {qbConnected && viewState === 'empty' && (syncing || syncError) && (
        <div style={{ background: syncError ? "rgba(180,60,60,0.06)" : "rgba(92,122,90,0.06)", borderBottom:`1px solid ${syncError ? "rgba(180,60,60,0.25)" : "rgba(92,122,90,0.25)"}`, padding:"11px 36px", display:"flex", alignItems:"center", justifyContent:"space-between" }} className="app-banner">
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {syncing ? <span className="spinner"/> : <div style={{ width:6, height:6, borderRadius:"50%", background: syncError ? RED : ACCENT2 }}/>}
            <div style={{ fontSize:13, color: syncError ? RED : ACCENT2, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>
              {syncing ? "Syncing your QuickBooks data…" : syncError}
            </div>
          </div>
        </div>
      )}

      {syncProblems.length > 0 && (
        <div className="app-banner" style={{ background:"rgba(196,144,32,0.08)", borderBottom:"1px solid rgba(196,144,32,0.35)", padding:"10px 36px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:AMBER, flexShrink:0 }}/>
            <div style={{ fontSize:12, color:MID, fontFamily:"'DM Sans',sans-serif", flex:1 }}>
              Sync finished, but some steps didn't complete, so some job costs may be out of date. Try syncing again; if this keeps happening, contact support.
              {" "}<span onClick={()=>setShowSyncDetails(v=>!v)} style={{ cursor:"pointer", color:ACCENT, textDecoration:"underline", fontWeight:500 }}>{showSyncDetails ? "Hide details" : "Show details"}</span>
            </div>
            <button aria-label="Dismiss" onClick={()=>{setSyncProblems([]);setShowSyncDetails(false);}} style={{ background:"none",border:"none",cursor:"pointer",color:DIM,fontSize:14,padding:"0 4px",flexShrink:0 }}>x</button>
          </div>
          {showSyncDetails && (
            <div style={{ marginTop:8, marginLeft:16, fontSize:11, color:MID, fontFamily:"monospace", lineHeight:1.6, userSelect:"text" }}>
              {syncProblems.map((p, i) => <div key={i}>{p.code}{p.detail ? `: ${p.detail}` : ""}</div>)}
            </div>
          )}
        </div>
      )}

      {syncNudge && (
        <div className="app-banner" style={{ background:CARD,borderBottom:`1px solid ${BORDER}`,padding:"7px 36px",display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ width:6,height:6,borderRadius:"50%",background:ACCENT2,flexShrink:0 }}/>
          <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif",flex:1 }}>
            Sync complete —{" "}
            {syncNudge.auto > 0 ? `${syncNudge.auto} auto-matched` : ""}
            {syncNudge.auto > 0 && (syncNudge.suggested > 0 || syncNudge.needs_attention > 0) ? ", " : ""}
            {syncNudge.suggested > 0 ? `${syncNudge.suggested} need confirmation` : ""}
            {syncNudge.suggested > 0 && syncNudge.needs_attention > 0 ? ", " : ""}
            {syncNudge.needs_attention > 0 ? `${syncNudge.needs_attention} need attention` : ""}
            {" · "}<span onClick={()=>{setTab("inbox");setSyncNudge(null);}} style={{ cursor:"pointer",color:ACCENT,textDecoration:"underline",fontWeight:500 }}>Review</span>
          </div>
          <button onClick={()=>setSyncNudge(null)} style={{ background:"none",border:"none",cursor:"pointer",color:DIM,fontSize:14,padding:"0 4px",flexShrink:0 }}>x</button>
        </div>
      )}

      <div style={{ flex:1 }}>
        {tab!=="upload" && viewState!=="live" && (
          <DataStateNotice state={viewState} tab={tab} clientType={clientType} qbConnected={qbConnected} qbStarting={qbStarting} syncing={syncing}
            onConnect={connectQuickBooks} onSync={()=>triggerSync(session?.user?.id)} onUpload={()=>setTab("upload")} onRetry={refreshData}/>
        )}
        {tab==="dashboard" && viewState==="live" && <Dashboard businessName={contractorName} greetingName={demo ? DEMO_BUSINESS_NAME : (profile?.name || "")} onJobClick={handleJobClick} onEstimate={()=>setTab("estimator")} onJumpToInbox={()=>setTab("inbox")} onClientClick={()=>setTab("clients")} onCompare={()=>setShowComparison(true)} jobSummaries={jobSummaries} untagged={[...untagged, ...(suggested||[])]} overhead={overhead} dismissed={dismissed} qbConnected={qbConnected} dataSource={dataSource} userId={session?.user?.id} onConnectQuickBooks={connectQuickBooks} qbStarting={qbStarting} clientType={clientType} dateRange={dateRange} setDateRange={setDateRange} customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd} revenueGoals={revenueGoals} onAddGoal={()=>{setEditingGoal(null);setShowGoalModal(true);}} onEditGoal={g=>{setEditingGoal(g);setShowGoalModal(true);}} onDeleteGoal={handleDeleteGoal} onMarkCompleteGoal={handleMarkCompleteGoal} onArchiveGoal={handleArchiveGoal}/>}
        {showGoalModal && <RevenueGoalModal goal={editingGoal} onSave={handleSaveGoal} onClose={()=>setShowGoalModal(false)}/>}
        {showComparison && <JobComparisonModal jobSummaries={jobSummaries} onClose={()=>setShowComparison(false)}/>}
        {tab==="inbox" && viewState==="live" && <SyncReview autoMatched={autoMatched} suggested={suggested} untagged={untagged} allTagged={allTagged} overhead={overhead} dismissed={dismissed} jobSummaries={jobSummaries} vendorRules={vendorRules} onConfirmSuggestion={handleConfirmSuggestion} onTag={handleTag} onMarkOverhead={handleMarkOverhead} onDismiss={handleDismiss} onRestore={handleRestore} onRetag={handleRetag} onUndoAutoMatch={handleUndoAutoMatch} onSaveVendorRule={handleSaveVendorRule} onAddExpense={handleAddExpense} dateRange={dateRange} setDateRange={setDateRange} customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd}/>}
        {tab==="detail" && viewState==="live" && <JobDetail job={selectedJob} onBack={()=>setTab("dashboard")} pendingItems={[...untagged, ...(suggested||[])]} onJumpToInbox={clientType==="quickbooks"?()=>setTab("inbox"):null} onAddLabor={handleAddLabor} onDeleteLabor={handleDeleteLabor} onAddExpense={handleAddExpense} onDeleteExpense={handleDeleteExpense} onAddRevenue={handleAddRevenue} onDeleteRevenue={handleDeleteRevenue} onUpdateLabor={handleUpdateLabor} onUpdateExpense={handleUpdateExpense} onUpdateRevenue={handleUpdateRevenue} onUpdateJobType={handleUpdateJobType} jobSummaries={jobSummaries} onJobClick={handleJobClick} periodNote={periodNote}/>}
        {tab==="clients" && viewState==="live" && <ClientScorecard jobSummaries={jobSummaries} pendingItems={[...untagged, ...(suggested||[])]}/>}
        {tab==="estimator" && viewState==="live" && <JobEstimator demo={demo} demoEstimates={demoState?.estimates} demoTemplates={demoState?.templates} jobSummaries={jobSummaries} userId={session?.user?.id} contractorName={contractorName}/>}
        {tab==="reports" && viewState==="live" && <Reports jobSummaries={jobSummaries} pendingItems={[...untagged, ...(suggested||[])]}/>}
        {tab==="chat" && viewState==="live" && <AIChat businessName={contractorName} demoAnswerList={demoAnswerList} jobSummaries={jobSummaries} needsReview={reviewItems} qualityJobs={qualityJobs} dateRange={dateRange} dataSource={dataSource} onOpenExpenses={clientType === "quickbooks" ? ()=>setTab("inbox") : undefined}/>}
        {tab==="raw" && viewState==="live" && <RawData jobSummaries={jobSummaries}/>}
        {tab==="upload"    && !demo && <UploadData userId={session?.user?.id} onDataRefresh={refreshData}/>}
        {tab==="upload"    && demo && (
          <div className="r-page" style={{ padding:"36px", maxWidth:640 }}>
            <h1 style={{ fontFamily:"'Lora',serif", fontSize:24, fontWeight:600, color:DARK, letterSpacing:"-0.02em", marginBottom:10 }}>Upload data</h1>
            <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:MID, lineHeight:1.7 }}>{DEMO_UPLOAD_NOTE}</p>
          </div>
        )}
      </div>

      {!bottomNav && (
        <div style={{ borderTop:`1px solid ${BORDER}`, padding:"14px 36px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, background:CARD }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, lineHeight:1.6, display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:4, height:4, borderRadius:"50%", background:BORDER, flexShrink:0 }}/>
            {demo ? "Sample numbers for a made-up business. Not financial advice." : (clientType === "quickbooks" ? "Figures sourced from QuickBooks Online. Not financial advice." : "Figures come from the data you entered or uploaded. Not financial advice.")}
            <span style={{ color:ACCENT, cursor:"pointer" }} onClick={() => setShowDisclaimer(true)}>Full notice</span>
          </div>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM }}>
            <a href="mailto:support@canopybi.com" style={{ color:DIM, textDecoration:"none" }}>support@canopybi.com</a>
          </div>
        </div>
      )}

      {/* ── Bottom bar (phones and tablets) ── */}
      {bottomNav && (
        <nav className="mob-nav" aria-label="Main navigation">
          {navSplit.bar.map(t => (
            <button key={t.key} type="button" className={`mob-nav-item${tab===t.key?" active":""}`} aria-current={tab===t.key ? "page" : undefined} aria-label={t.key==="inbox" && reviewCount > 0 ? `${barLabel(t)}, ${reviewCount} to review` : undefined} onClick={() => goTab(t.key)}>
              {t.icon}
              <span>{barLabel(t)}</span>
              {t.key==="inbox" && reviewCount > 0 && <span className="badge" aria-hidden="true">{reviewCount}</span>}
            </button>
          ))}
          <button type="button" className={`mob-nav-item${(moreSheetOpen || moreIsActive(navSplit.more, tab)) ? " active" : ""}`} data-more="true" ref={moreBtnRef} aria-haspopup="dialog" aria-expanded={moreSheetOpen} onClick={() => setMoreOpen(o => !o)}>
            {ICONS.more}
            <span>More</span>
          </button>
        </nav>
      )}

      {/* ── More sheet: every other screen, plus the account actions the sidebar has on desktop ── */}
      {moreSheetOpen && (
        <div className="more-backdrop" onClick={closeMore}>
          <div className="more-sheet" ref={moreSheetRef} role="dialog" aria-modal="true" aria-label="More" onClick={e => e.stopPropagation()} onKeyDown={moreSheetKeys}>
            <div className="more-head">
              <span style={{ fontFamily:"'Lora',serif", fontSize:18, fontWeight:500, color:DARK }}>More</span>
              <button type="button" autoFocus className="r-close" aria-label="Close" onClick={closeMore} style={{ background:"none", border:"none", cursor:"pointer", color:DIM, fontSize:26, lineHeight:1, minWidth:44, minHeight:44 }}>×</button>
            </div>
            {navSplit.more.map(t => (
              <button key={t.key} type="button" className={`more-item${tab===t.key?" active":""}`} aria-current={tab===t.key ? "page" : undefined} onClick={() => goTab(t.key)}>
                {t.icon}
                <span style={{ flex:1 }}>{t.label}</span>
                {t.key==="detail" && selectedJob && <span style={{ fontSize:13, color:DIM }}>{selectedJob.name}</span>}
              </button>
            ))}
            <div className="more-rule"/>
            <div className="more-label">{contractorName || "Your account"}{demo ? " · sample numbers" : ""}</div>
            {clientType === "quickbooks" && !demo && (
              <div style={{ padding:"0 20px 6px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color: qbConnected ? ACCENT2 : AMBER }}>
                {syncing ? "Syncing…" : qbConnected ? "QuickBooks connected" : "QuickBooks not connected"}
              </div>
            )}
            {clientType === "quickbooks" && qbConnected && !demo && !syncing && (
              <button type="button" className="more-item" onClick={() => { closeMore(); triggerSync(session?.user?.id); }}>Sync QuickBooks now</button>
            )}
            {showSettingsEntry({ demo, signedIn: !!session }) && (
              <button type="button" className="more-item" onClick={() => { closeMore(); openSettings(); }}>{ICONS.gear}<span>Settings</span></button>
            )}
            {!demo && <button type="button" className="more-item" onClick={() => { closeMore(); setShowBusinessType(true); }}>Business type</button>}
            <button type="button" className="more-item" onClick={() => { closeMore(); setShowTutorial(true); }}>Help and tour</button>
            <button type="button" className="more-item" onClick={() => { closeMore(); setShowDisclaimer(true); }}>About your data</button>
            <a className="more-item" href="mailto:support@canopybi.com">Contact support</a>
            {demo ? (
              <a className="more-item" href={DEMO_SIGNUP_HREF} style={{ color:ACCENT, fontWeight:500 }}>{DEMO_SIGNUP_LABEL}</a>
            ) : (
              <button type="button" className="more-item" onClick={() => { closeMore(); handleSignOut(); }}>Sign out</button>
            )}
          </div>
        </div>
      )}

      </div>
    </div>
    </BusinessTypeContext.Provider>
  );
}
